import { useState } from 'react'
import { CATEGORY_MAP, type Category } from '@/lib/constants'
import { Dialog, DialogHeader, DialogTitle, DialogClose, DialogContent } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { formatDuration } from '@/lib/utils'
import { Clock, Save } from 'lucide-react'

interface RecordFormProps {
  category: Category
  duration: number
  onSubmit: (details: Record<string, string>) => Promise<unknown>
  onClose: () => void
}

export function RecordForm({ category, duration, onSubmit, onClose }: RecordFormProps) {
  const config = CATEGORY_MAP[category]
  const Icon = config.icon
  const [details, setDetails] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      await onSubmit(details)
    } finally {
      setSubmitting(false)
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open={true} onOpenChange={handleOpenChange}>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${config.gradient} flex items-center justify-center`}>
            <Icon className="h-4 w-4 text-white" />
          </div>
          <span>结束{config.label}记录</span>
        </DialogTitle>
        <DialogClose onClick={onClose} />
      </DialogHeader>
      <DialogContent>
        <div className="flex items-center gap-3 p-4 rounded-xl bg-muted/50 mb-4">
          <Clock className="h-5 w-5 text-muted-foreground" />
          <span className="text-2xl font-bold tabular-nums">{formatDuration(duration)}</span>
        </div>

        <div className="space-y-4">
          {config.detailsFields.map((field) => (
            <div key={field.key}>
              <label className="block text-sm font-medium mb-1.5">{field.label}</label>
              {field.type === 'select' && field.options ? (
                <Select
                  value={details[field.key] || ''}
                  onChange={(e) => setDetails(prev => ({ ...prev, [field.key]: e.target.value }))}
                >
                  <option value="">{field.placeholder}</option>
                  {field.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Select>
              ) : (
                <Input
                  placeholder={field.placeholder}
                  value={details[field.key] || ''}
                  onChange={(e) => setDetails(prev => ({ ...prev, [field.key]: e.target.value }))}
                />
              )}
            </div>
          ))}
        </div>

        <div className="flex gap-3 mt-6">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={submitting}>
            跳过
          </Button>
          <Button className="flex-1 gap-2" onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <span className="animate-spin">...</span>
            ) : (
              <Save className="h-4 w-4" />
            )}
            保存记录
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
